import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { resultadosService, type ListResponse } from '../services/resultados.service'
import { addToSyncQueue, cacheApiResponse, getCachedApiResponse } from '../../../lib/offlineDb'
import type { ResultadoFilters, CreateResultadoPayload, UpdateResultadoPayload, Resultado, MapaResultadoItem } from '../types'

export const RESULTADOS_KEY = 'resultados'
export const MAPA_KEY = 'mapa-resultados'

function isOfflineError(err: unknown): boolean {
  if (typeof err === 'object' && err !== null) {
    const e = err as Record<string, unknown>
    if (e.message === 'Network Error') return true
    if (typeof e.message === 'string' && e.message.includes('Network')) return true
    if (!navigator.onLine) return true
  }
  return !navigator.onLine
}

export function useResultados(filters: ResultadoFilters = {}) {
  return useQuery<ListResponse<Resultado>>({
    queryKey: [RESULTADOS_KEY, filters],
    queryFn: async () => {
      const cacheKey = `resultados:${JSON.stringify(filters)}`
      try {
        const data = await resultadosService.list(filters)
        await cacheApiResponse(cacheKey, data)
        return data
      } catch (err) {
        if (isOfflineError(err)) {
          const cached = await getCachedApiResponse(cacheKey)
          if (cached) return cached as ListResponse<Resultado>
        }
        throw err
      }
    },
  })
}

export function useMapaResultados(idetapa: number | null) {
  return useQuery<MapaResultadoItem[]>({
    queryKey: [MAPA_KEY, idetapa],
    queryFn: async () => {
      const cacheKey = `mapa:${idetapa}`
      try {
        const data = await resultadosService.getMapaByEtapa(idetapa!)
        await cacheApiResponse(cacheKey, data)
        return data
      } catch (err) {
        if (isOfflineError(err)) {
          const cached = await getCachedApiResponse(cacheKey)
          if (cached) return cached as MapaResultadoItem[]
        }
        throw err
      }
    },
    enabled: idetapa !== null,
    staleTime: 0,
  })
}

export function useCreateResultado() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateResultadoPayload) => {
      try {
        const result = await resultadosService.create(data)
        return result
      } catch (err) {
        if (isOfflineError(err)) {
          await addToSyncQueue({
            table: 'resultados',
            action: 'create',
            payload: data as unknown as Record<string, unknown>,
            endpoint: '/api/v1/resultados',
            method: 'POST',
          })
          return { queued: true, syncPending: true } as unknown as ReturnType<typeof resultadosService.create>
        }
        throw err
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [MAPA_KEY] })
      qc.invalidateQueries({ queryKey: [RESULTADOS_KEY] })
      qc.invalidateQueries({ queryKey: ['ranking-etapa'] })
    },
  })
}

export function useUpdateResultado() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateResultadoPayload }) => {
      try {
        const result = await resultadosService.update(id, data)
        return result
      } catch (err) {
        if (isOfflineError(err)) {
          await addToSyncQueue({
            table: 'resultados',
            action: 'update',
            payload: { id, ...data } as unknown as Record<string, unknown>,
            endpoint: `/api/v1/resultados/${id}`,
            method: 'PUT',
          })
          return { queued: true, syncPending: true } as unknown as ReturnType<typeof resultadosService.update>
        }
        throw err
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [MAPA_KEY] })
      qc.invalidateQueries({ queryKey: [RESULTADOS_KEY] })
      qc.invalidateQueries({ queryKey: ['ranking-etapa'] })
    },
  })
}

export function useReprocessarResultados() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (idmodalidade: number) => resultadosService.reprocessar(idmodalidade),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [MAPA_KEY] })
      qc.invalidateQueries({ queryKey: [RESULTADOS_KEY] })
      qc.invalidateQueries({ queryKey: ['ranking-etapa'] })
    },
  })
}

export function useTopResultados(limit = 20) {
  return useQuery<Resultado[]>({
    queryKey: [RESULTADOS_KEY, 'top', limit],
    queryFn: () => resultadosService.getTop(limit),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })
}
